// src/components/news/Article.jsx
import React, { useEffect, useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';

import { getByNewsSiteName } from '../../utils/api';
import DOMPurify from 'dompurify';
import Tests from '../shorts/Tests';
import CommentSection from '../comments/CommentSection';
import { Helmet } from 'react-helmet-async';
import NewsJsonLd from '../../components/NewsJsonLd';
import AdSlot from '../ads/AdSlot';

import '../../assets/css/news.css';

// --- Helper: YouTube-Iframes erlauben, Rest säubern ---
const YT_IFRAME_SRC_REGEX =
    /^(?:(?:https?:)?\/\/)(?:www\.)?(?:youtube\.com\/embed\/|youtube-nocookie\.com\/embed\/|youtu\.be\/)/i;

function sanitizeAllowYouTube(html) {
    if (!html) return '';
    const sanitized = DOMPurify.sanitize(html, {
        USE_PROFILES: { html: true },
        ADD_TAGS: ['iframe'],
        ADD_ATTR: [
            'src', 'width', 'height', 'allow', 'allowfullscreen',
            'frameborder', 'loading', 'referrerpolicy', 'title'
        ],
        FORBID_ATTR: [/^on/i],
    });

    try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(sanitized, 'text/html');
        const iframes = doc.body.querySelectorAll('iframe');

        iframes.forEach((el) => {
            const src = el.getAttribute('src') || '';
            if (!YT_IFRAME_SRC_REGEX.test(src)) {
                el.remove();
                return;
            }
            if (!el.hasAttribute('loading')) el.setAttribute('loading', 'lazy');
            if (!el.hasAttribute('referrerpolicy')) el.setAttribute('referrerpolicy', 'no-referrer-when-downgrade');
            if (!el.hasAttribute('allow')) {
                el.setAttribute(
                    'allow',
                    'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
                );
            }
            el.setAttribute('frameborder', '0');
            el.setAttribute('allowfullscreen', '');
            if (!el.hasAttribute('title')) el.setAttribute('title', 'YouTube Video');
        });
        return doc.body.innerHTML;
    } catch {
        return sanitized;
    }
}

const textOnly = (html) => {
    if (!html) return '';
    const t = DOMPurify.sanitize(html, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] })
        .replace(/\s+/g, ' ')
        .trim();
    return t;
};

const getDescription = (html, maxLength = 160) => {
    const t = textOnly(html);
    if (!t) return '';
    return t.length > maxLength ? (t.slice(0, maxLength).replace(/\s+\S*$/, '') + '…') : t;
};

const iso = (s) => (s ? new Date(s).toISOString() : undefined);

// --- Neu: robust normalisieren (Array, String, JSON-String, Objekt) ---
const toArray = (val) => {
    if (!val) return [];
    if (Array.isArray(val)) return val.filter(Boolean).map(String);

    if (typeof val === 'string') {
        // Versuch: JSON-Array im String
        try {
            const parsed = JSON.parse(val);
            if (Array.isArray(parsed)) return parsed.filter(Boolean).map(String);
        } catch { /* ignore */ }
        // sonst mit gängigen Separatoren splitten
        return val
            .split(/[;,#]/)
            .map(s => s.trim())
            .filter(Boolean);
    }

    if (typeof val === 'object') {
        // z.B. {0:'tag1',1:'tag2'}
        return Object.values(val).map(String).filter(Boolean);
    }

    return [];
};

const Article = () => {
    const { siteName } = useParams();
    const [news, setNews] = useState(null);

    useEffect(() => {
        const fetchNewsArticle = async (sn) => {
            try {
                const data = await getByNewsSiteName(sn);
                setNews(data || null);
            } catch (err) {
                console.error(err);
            }
        };
        if (siteName) fetchNewsArticle(siteName);
    }, [siteName]);

    const formatDateHuman = (isoString) => {
        const date = new Date(isoString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${day}.${month}.${year} um ${hours}:${minutes} Uhr`;
    };

    const cleanHtml = useMemo(
        () => (news?.content ? sanitizeAllowYouTube(news.content) : ''),
        [news?.content]
    );

    // Basis-URL (für canonical & absolute Bild-URLs)
    const siteOrigin =
        (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_SITE_URL) ||
        window.location.origin;

    // Slug unverändert verwenden
    const slug = news?.site_name || siteName || '';
    const canonicalUrl = `${siteOrigin}/news/${slug}`;
    const imageAbsoluteUrl = news?.image ? `${siteOrigin}/news/bilder/${news.image}` : undefined;

    // abgeleitete Felder
    const desc160 = getDescription(news?.content, 160);
    const desc240 = getDescription(news?.content, 240);
    const publishedISO = iso(news?.date);
    const modifiedISO = iso(news?.updated_at || news?.date);
    const articleText = textOnly(news?.content);
    const wordCount = articleText ? articleText.split(/\s+/).filter(Boolean).length : undefined;

    // Neu: Tags robust normalisieren (tags/keywords)
    const tags = useMemo(() => {
        const t1 = toArray(news?.tags);
        if (t1.length) return t1;
        return toArray(news?.keywords);
    }, [news?.tags, news?.keywords]);

    return (
        <>
            {news && (
                <Helmet>
                    {/* Titel & Canonical */}
                    <title>{news.headline}</title>
                    <meta name="description" content={desc160} />
                    <link rel="canonical" href={canonicalUrl} />

                    {/* Open Graph */}
                    <meta property="og:title" content={news.headline} />
                    <meta property="og:description" content={desc160} />
                    <meta property="og:type" content="article" />
                    <meta property="og:url" content={canonicalUrl} />
                    {imageAbsoluteUrl && <meta property="og:image" content={imageAbsoluteUrl} />}
                    {imageAbsoluteUrl && <meta property="og:image:alt" content={news.headline} />}

                    {/* Twitter Cards */}
                    <meta name="twitter:card" content={imageAbsoluteUrl ? 'summary_large_image' : 'summary'} />
                    <meta name="twitter:title" content={news.headline} />
                    <meta name="twitter:description" content={desc160} />
                    {imageAbsoluteUrl && <meta name="twitter:image" content={imageAbsoluteUrl} />}

                    {/* Article meta (hilft OG/Facebook & einige Crawler) */}
                    {publishedISO && <meta property="article:published_time" content={publishedISO} />}
                    {modifiedISO && <meta property="article:modified_time" content={modifiedISO} />}
                    {news?.username && <meta property="article:author" content={news.username} />}
                    <meta property="article:section" content="News" />
                    {(tags || []).slice(0, 10).map((t, i) => (
                        <meta key={i} property="article:tag" content={String(t)} />
                    ))}
                </Helmet>
            )}

            {/* === JSON-LD: NewsArticle + Breadcrumb === */}
            {news && (
                <>
                    <NewsJsonLd
                        url={canonicalUrl}
                        headline={news.headline}
                        description={desc240}
                        articleSection="News"
                        keywords={(tags && tags.length) ? tags : undefined}
                        inLanguage="de"
                        datePublished={publishedISO}
                        dateModified={modifiedISO}
                        images={imageAbsoluteUrl ? [{ url: imageAbsoluteUrl, width: 1600, height: 900 }] : []}
                        authors={[
                            {
                                name: news.username,
                                url: news.user_slug ? `${siteOrigin}/user/${news.user_slug}` : undefined,
                            },
                        ]}
                        publisherName="NGC TV"
                        publisherLogoUrl={`${siteOrigin}/static/logo-512.png`}
                        publisherLogoWidth={512}
                        publisherLogoHeight={512}
                        isAccessibleForFree={true}
                        // Zusätze (falls von deiner NewsJsonLd-Komponente unterstützt):
                        mainEntityOfPage={canonicalUrl}
                        wordCount={wordCount}
                        articleBody={articleText}
                    />

                    <Helmet>
                        <script type="application/ld+json">
                            {JSON.stringify({
                                '@context': 'https://schema.org',
                                '@type': 'BreadcrumbList',
                                itemListElement: [
                                    { '@type': 'ListItem', position: 1, name: 'Start', item: siteOrigin + '/' },
                                    { '@type': 'ListItem', position: 2, name: 'News', item: siteOrigin + '/news' },
                                    { '@type': 'ListItem', position: 3, name: news.headline, item: canonicalUrl },
                                ],
                            })}
                        </script>
                    </Helmet>
                </>
            )}

            <main>
                <div className="wrapper">
                    <div className="rows">
                        <div className="row">
                            <div className="category">
                                News
                            </div>

                            <div className="news">
                                {news ? (
                                    <article
                                        className="news-entry news-article"
                                        key={news.id}
                                        itemScope
                                        itemType="https://schema.org/NewsArticle"
                                    >
                                        <header>
                                            <h1 itemProp="headline">{news.headline}</h1>

                                            <div className="news-author-date">
                                                {news.user_image && (
                                                    <img
                                                        className="author-image"
                                                        src={news.user_image}
                                                        alt={news.username}
                                                        width="40"
                                                        height="40"
                                                        loading="lazy"
                                                        decoding="async"
                                                    />
                                                )}
                                                <strong itemProp="author" itemScope itemType="https://schema.org/Person">
                                                    <span itemProp="name">{news.username}</span>
                                                </strong>
                                                <span aria-hidden>,&nbsp;</span>
                                                {news.date && (
                                                    <time
                                                        itemProp="datePublished"
                                                        dateTime={publishedISO}
                                                        title={formatDateHuman(news.date)}
                                                    >
                                                        {formatDateHuman(news.date)}
                                                    </time>
                                                )}
                                                {modifiedISO && modifiedISO !== publishedISO && (
                                                    <>
                                                        <span aria-hidden>&nbsp;•&nbsp;</span>
                                                        <time
                                                            itemProp="dateModified"
                                                            dateTime={modifiedISO}
                                                            title={`Aktualisiert: ${formatDateHuman(news.updated_at)}`}
                                                        >
                                                            Aktualisiert
                                                        </time>
                                                    </>
                                                )}
                                            </div>

                                            {news.image && (
                                                <figure className="news-hero">
                                                    <img
                                                        src={`/news/bilder/${news.image}`}
                                                        alt={news.headline}
                                                        width="1600"
                                                        height="900"
                                                        loading="eager"
                                                        fetchpriority="high"
                                                        decoding="async"
                                                        itemProp="image"
                                                    />
                                                </figure>
                                            )}
                                        </header>

                                        <section
                                            className="news-body"
                                            itemProp="articleBody"
                                            dangerouslySetInnerHTML={{ __html: cleanHtml }}
                                        />

                                        <footer>
                                            {(Array.isArray(tags) && tags.length > 0) && (
                                                <nav className="news-tags" aria-label="Tags">
                                                    {tags.map((t, i) => (
                                                        <Link key={i} to={`/news?tag=${encodeURIComponent(t)}`} className="tag">
                                                            #{t}
                                                        </Link>
                                                    ))}
                                                </nav>
                                            )}
                                            <CommentSection postId={news.id} postType="news" />
                                        </footer>
                                    </article>
                                ) : (
                                    <p>Lade Artikel...</p>
                                )}
                            </div>
                        </div>

                        <div className="row">
                            <Tests />
                            <AdSlot slot="2119524913" />
                        </div>
                    </div>
                </div>
            </main>
        </>
    );
};

export default Article;
